import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useEffect, useState, useRef } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { AxiosResponse } from "axios";
import CreateExtractionService from "../../../../Services/InfectiousDisease/CreateExtractionService";
import { closeMenuOnScroll } from "Pages/Blood/BloodCompendium/Test/Shared";
import { validateFields } from "Pages/LIS/Toxicology/CommonFunctions";
import useLang from "Shared/hooks/useLanguage";
import { reactSelectSMStyle, styles } from "Utils/Common";

function CreateExtractionRacksData() {
  const queryClient = useQueryClient();

  const initialValue = {
    selectedRun: -1, // ✅ default to --Select--
    runName: "",
    totalWell: null, // ✅ NEW (16 | 32)
  };

  const [data, setData] = useState<any[]>([]);
  const [createRun, setCreateRun] = useState(initialValue);

  // ================== GET RACK LOOKUP - START ====================
  const { data: runLookupData } = useQuery(
    ["getIDRacksLookup"],
    CreateExtractionService.GetIDRacksLookup,
    {
      staleTime: 5 * 60 * 1000, // optional optimization
    }
  );
  // ================== GET RACK LOOKUP - END ====================

  const dropDownValues = [
    { value: -1, label: "--Select--" }, // ✅ new default option
    { value: 0, label: "Add New Rack" },
    ...(runLookupData?.data || []),
  ];

  // GET WELL DATA
  const { data: wellData } = useQuery(
    ["wellNo", createRun.selectedRun],
    () =>
      CreateExtractionService.GetRackPlateDetailsById(createRun.selectedRun),
    {
      enabled: createRun.selectedRun > 0,
    }
  );

  // Sync API data → local state (no behavior change)
  useEffect(() => {
    if (wellData?.data?.data) {
      const updatedData = wellData.data.data.map((x: any) => ({
        // Store the ORIGINAL value when data loads.
        ...x,
        accession: x.accession || "",
        originalAccession: x.accession || "", // IMPORTANT FIX
      }));
      setData(updatedData);
    }
  }, [wellData]);

  //  ==============  CREATE NEW Rack - START  ==============
  type CreateRackPayload = {
    rackName: string;
    totalWell: number;
  };

  const createRunMutation = useMutation({
    mutationFn: (payload: CreateRackPayload) =>
      CreateExtractionService.SaveRackCreation(payload),

    onSuccess: (res: any) => {
      if (res?.data?.statusCode === 200) {
        queryClient.invalidateQueries({ queryKey: ["getIDRacksLookup"] });
        setCreateRun(initialValue);
        toast.success(res?.data?.message);
      } else {
        toast.error(res?.data?.message || "Something went wrong.");
      }
    },

    onError: () => {
      toast.error("An unexpected error occurred.");
    },
  });
  //  ==============  CREATE NEW Rack - END  ==============

  // =============== Start "Save" & "Save Plate" Code ======================
  const handleSavePlate = () => {
    const hasAtLeastOneAccession = data.some(
      (x) => x.plateNo !== "Rack ID" && x.accession?.trim()
    );
    if (!hasAtLeastOneAccession) {
      toast.error("Please fill at least one Accession No field");
      return;
    }
    const payloadSavePlate = {
      rackDetailId: createRun.selectedRun,
      willRackCompleted: true, // Client Request: They want to Save & Complete Rack.
      wellnoWithAccessionRequest: data,
    };
    saveMutation.mutate(payloadSavePlate);
  };

  const saveMutation = useMutation(
    CreateExtractionService.SaveRackPlateDetails,
    {
      onSuccess: (res: any) => {
        if (res?.data?.statusCode === 200) {
          queryClient.invalidateQueries("getIDRacksLookup");
          setCreateRun(initialValue);
          toast.success(res?.data?.message);
        } else {
          toast.error(res?.data?.message || "Something went wrong.");
        }
      },
      onError: () => {
        toast.error("An unexpected error occurred.");
      },
    }
  );
  // =============== END "Save" & "Save Plate" Code ======================

  const handleChange = (field: string, value: any) => {
    setCreateRun((prev) => ({ ...prev, [field]: value }));
  };

  const handleChangeAccession = (name: string, value: string, id: number) => {
    setData((curr) =>
      curr.map((x) => (x.id === id ? { ...x, [name]: value } : x))
    );
  };

  // ================================================= START - Add New Rack ====================================================
  const handleAddNewRack = () => {
    const { isValid } = validateFields({
      runName: createRun.runName,
    });

    if (!isValid) {
      toast.error("Please Fill Rack Name Field");
      return;
    }

    if (!createRun.totalWell) {
      toast.error("Please select Wells (16 or 32)");
      return;
    }

    const payload = {
      rackName: createRun.runName,
      totalWell: createRun.totalWell, // NEW FIELD (16 | 32)
    };

    createRunMutation.mutate(payload);
  };
  // ================================================= END - Add New Rack ====================================================

  // ===============================
  // BASE64 → EXCEL DOWNLOAD (FIXED)
  // ===============================
  const base64ToExcel = (base64: string, filename: string) => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // *********** React Query for Export / Download using React Query mutation ***********
  const { t } = useLang();
  const downloadRackDetailsByIdMutation = useMutation({
    mutationFn: (rackDetailId: number) =>
      CreateExtractionService.ExportRackPlateDetailsById(rackDetailId),

    onSuccess: (res: AxiosResponse) => {
      if (res?.data?.statusCode === 200) {
        toast.success(t(res?.data?.message));
        base64ToExcel(res.data.data.fileContents, "RackDetails");
      } else {
        toast.error(t(res?.data?.message));
      }
    },
  });

  const downloadRackDetailsById = () => {
    downloadRackDetailsByIdMutation.mutate(createRun.selectedRun);
  };
  // *********** React Query for Export / Download using React Query mutation ***********

  // =========================================== Start - UpdateRackPlateAccession =======================================
  const lastSavedRef = useRef<number | null>(null);
  const inputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const updateAccessionMutation = useMutation({
    mutationFn: (payload: any) =>
      CreateExtractionService.UpdateRackPlateAccession(payload),
  });

  const saveAccession = async (row: any) => {
    const accessionNo = row.accession?.trim();
    const originalAccession = row.originalAccession?.trim();

    //if (!accessionNo) return false;

    // ✅ DO NOT CALL API if value not changed
    if (accessionNo === originalAccession) {
      return true;
    }

    if (lastSavedRef.current === row.id) return false;

    lastSavedRef.current = row.id;

    const payload = {
      rackDetailId: createRun.selectedRun,
      id: row.id,
      accessionNo,
    };

    try {
      const res: any = await updateAccessionMutation.mutateAsync(payload);

      lastSavedRef.current = null;

      // ================= SUCCESS =================
      if (res?.data?.statusCode === 200) {
        const updated = res.data.data;

        setData((prev) =>
          prev.map((x) =>
            x.id === updated.id
              ? {
                  ...x,
                  scannedDate: updated.scannedDate,
                  scannedBy: updated.scannedBy,

                  // ✅ update original value after successful save
                  originalAccession: accessionNo,
                }
              : x
          )
        );

        toast.success("Saved Successfully");
        return true;
      }

      // ================= DUPLICATE =================
      if (res?.data?.statusCode === 400) {
        toast.error("Duplicate Accession No");

        // Clear textbox
        setData((prev) =>
          prev.map((x) =>
            x.id === row.id
              ? {
                  ...x,
                  accession: "",
                }
              : x
          )
        );

        // Focus same textbox again
        setTimeout(() => {
          inputRefs.current[row.id]?.focus();
        }, 100);

        return false;
      }

      // ================= Accession No does not Exist in the System =================
      if (res?.data?.statusCode === 404) {
        toast.error("Accession No does not exist in the system.");

        // Clear textbox
        setData((prev) =>
          prev.map((x) =>
            x.id === row.id
              ? {
                  ...x,
                  accession: "",
                }
              : x
          )
        );

        // Focus same textbox again
        setTimeout(() => {
          inputRefs.current[row.id]?.focus();
        }, 100);

        return false;
      }

      return false;
    } catch {
      lastSavedRef.current = null;
      toast.error("Something went wrong");
      return false;
    }
  };

  const handleKeyDown = async (e: any, row: any, index: number) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const isSaved = await saveAccession(row);

    // Move ONLY if save successful
    if (isSaved) {
      moveToNext(index);
    }
  };

  const handleTabKey = async (e: any, row: any, index: number) => {
    if (e.key !== "Tab") return;

    e.preventDefault();
    const isSaved = await saveAccession(row);

    // Move ONLY if save successful
    if (isSaved) {
      moveToNext(index);
    }
  };

  const handleBlur = async (row: any) => {
    await saveAccession(row);
  };

  const moveToNext = (currentIndex: number) => {
    const nextRow = data[currentIndex + 1];

    if (!nextRow) return;
    const nextInput = inputRefs.current[nextRow.id];

    if (nextInput) {
      nextInput.focus();
    }
  };
  // =========================================== End - UpdateRackPlateAccession =======================================

  return (
    <>
      <div className="d-flex flex-column flex-column-fluid">
        <div className="app-content flex-column-fluid">
          <div className="app-container container-fluid">
            <div className="card rounded-top-0 shadow-none tab-content-card">
              <div className="card-body py-2">
                <Grid container spacing={2} className="mb-4">
                  {/* ✅ ROW 1: DROPDOWN ONLY */}
                  <Grid item xs={12} md={3}>
                    <Select
                      inputId="NGSRunCreateselectRun"
                      theme={(theme) => styles(theme)}
                      name="selectedRun"
                      options={dropDownValues}
                      styles={reactSelectSMStyle}
                      value={dropDownValues.find(
                        (x: any) => x.value === createRun.selectedRun
                      )}
                      menuPortalTarget={document.body}
                      closeMenuOnScroll={(e) => closeMenuOnScroll(e)}
                      onChange={(option: any) => {
                        setData([]); // Clear old rack immediately
                        handleChange("selectedRun", option.value);
                      }}
                    />
                  </Grid>

                  {/* ✅ ROW BREAK + HORIZONTAL LINE */}
                  {createRun.selectedRun === 0 && (
                    <Grid item xs={12}>
                      <hr className="my-3" />
                    </Grid>
                  )}

                  {/* ✅ ROW 2: INPUT + RADIO + BUTTON */}
                  {createRun.selectedRun === 0 && (
                    <>
                      <Grid item xs={12} md={3}>
                        <label className="form-label mb-1">Rack Name</label>
                        <input
                          type="text"
                          className="form-control h-30px rounded"
                          name="runName"
                          placeholder="Input"
                          value={createRun.runName}
                          onChange={(e) =>
                            handleChange("runName", e.target.value)
                          }
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={3}
                        className="d-flex align-items-end"
                      >
                        <div className="d-flex gap-4">
                          <label>
                            <input
                              type="radio"
                              name="totalWell"
                              checked={createRun.totalWell === 32}
                              onChange={() => handleChange("totalWell", 32)}
                            />{" "}
                            32 Wells
                          </label>

                          <label>
                            <input
                              type="radio"
                              name="totalWell"
                              checked={createRun.totalWell === 16}
                              onChange={() => handleChange("totalWell", 16)}
                            />{" "}
                            16 Wells
                          </label>
                        </div>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={2}
                        className="d-flex align-items-end"
                      >
                        <button
                          className="btn btn-primary btn-sm fw-bold px-5"
                          onClick={handleAddNewRack}
                          disabled={createRunMutation.isLoading}
                        >
                          Add New Rack
                        </button>
                      </Grid>
                    </>
                  )}
                </Grid>

                {createRun.selectedRun > 0 && data.length ? (
                  <>
                    <div className="card">
                      <Box sx={{ height: "auto", width: "100%" }}>
                        <div className="table_bordered overflow-hidden">
                          <TableContainer
                            sx={{
                              //////maxHeight: "calc(100vh - 100px)",                // Remove HEIGHT of Entire Box
                              "&::-webkit-scrollbar": { width: 7 },
                              "&::-webkit-scrollbar-track": {
                                backgroundColor: "#fff",
                              },
                              "&:hover": {
                                "&::-webkit-scrollbar-thumb": {
                                  backgroundColor: "var(--kt-gray-400)",
                                  borderRadius: 2,
                                },
                              },
                              "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "var(--kt-gray-400)",
                                borderRadius: 2,
                              },
                            }}
                            component={Paper}
                            className="shadow-none"
                          >
                            <Table
                              aria-label="sticky table collapsible"
                              className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
                            >
                              <TableHead>
                                <TableRow className="h-30px">
                                  <TableCell className="min-w-100px w-100px">
                                    Sr No.
                                  </TableCell>
                                  <TableCell className="min-w-100px w-100px">
                                    Plate No.
                                  </TableCell>
                                  <TableCell className="min-w-200px w-200px">
                                    Accession No
                                  </TableCell>
                                  <TableCell className="min-w-200px w-200px">
                                    Date Scanned
                                  </TableCell>
                                  <TableCell className="min-w-200px w-200px">
                                    User
                                  </TableCell>
                                </TableRow>
                              </TableHead>

                              <TableBody>
                                {data?.map((item: any) => (
                                  <TableRow key={item.id} className="h-40px">
                                    <TableCell className="min-w-100px w-100px">
                                      {item.wellNo}
                                    </TableCell>
                                    <TableCell className="min-w-100px w-100px">
                                      <span
                                        className={
                                          item.plateNo === "Rack ID"
                                            ? "fw-bold"
                                            : ""
                                        }
                                      >
                                        {item.plateNo}
                                      </span>
                                    </TableCell>

                                    <TableCell className="min-w-200px w-200px">
                                      {
                                        <input
                                          ref={(el) =>
                                            (inputRefs.current[item.id] = el)
                                          }
                                          id={`acc_${item.id}`}
                                          type="text"
                                          className="form-control h-25px rounded"
                                          name="accession"
                                          value={item.accession || ""}
                                          disabled={item.plateNo === "Rack ID"}
                                          onChange={(e) =>
                                            handleChangeAccession(
                                              e.target.name,
                                              e.target.value,
                                              item?.id
                                            )
                                          }
                                          onKeyDown={(e) => {
                                            //NOTE: This is used for Scanning/Saving Accession IN DB
                                            const index = data.findIndex(
                                              (x) => x.id === item.id
                                            );

                                            handleKeyDown(e, item, index);
                                            handleTabKey(e, item, index);
                                          }}
                                          onBlur={() => handleBlur(item)} //NOTE: This is used for Scanning/Saving Accession IN DB
                                        />
                                      }
                                    </TableCell>

                                    <TableCell className="min-w-200px w-200px">
                                      {item.scannedDate ?? ""}
                                    </TableCell>
                                    <TableCell className="min-w-200px w-200px">
                                      {item.scannedBy ?? ""}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </div>
                      </Box>
                    </div>

                    <button
                      className="btn btn-warning btn-sm fw-bold px-5 mt-3 me-2"
                      onClick={downloadRackDetailsById}
                      disabled={downloadRackDetailsByIdMutation.isLoading}
                    >
                      Download Plate File
                    </button>
                    <button
                      className="btn btn-primary btn-sm fw-bold px-5 mt-3"
                      onClick={handleSavePlate}
                      disabled={saveMutation.isLoading}
                    >
                      Save Plate
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default CreateExtractionRacksData;
